// SPL-Token-Marktdaten via Jupiter (Preis + Icon) für Solana-Token, die NICHT
// bei CoinGecko gelistet sind (NAKMAK, NOODLE, …).
//
// Hintergrund: Die Bestandsbewertung schlägt Preise via `COIN_MAP[sym].id →
// marketData[id].price` nach. SPL-Long-Tail steht weder in COIN_MAP noch bei
// CoinGecko → kein Preis (0 €) und kein Icon. Jupiter kennt diese Token aber.
//
// Eine EINZIGE Jupiter-Suche (`tokens/v2/search`) liefert alles auf einmal:
// `usdPrice` (USD!), `icon` (https), `symbol`, `stats24h.priceChange`, `mcap`.
// Wir rechnen den USD-Preis mit dem ECB-Tageskurs in EUR um (marketData ist in
// EUR) und bauen einen synthetischen `COIN_MAP`-Eintrag (id `spl:<mint>`) +
// marketData-Eintrag — exakt die Form, die Dashboard/Bewertung/CoinImage schon
// erwarten. KEINE neue Vercel-Function (12/12 voll): reiner Client-Fetch.
//
// Suche per SYMBOL funktioniert ohne gespeicherte Mint (greift auch für bereits
// importierte TX); die Mint — falls vorhanden — macht den Treffer eindeutig.
// img-src/connect-src für jup.ag + gateway.irys.xyz sind in vercel.json frei.

import { COIN_MAP } from './coinList';
import { getFiatToEurRate } from './fxConvert';

const JUP_SEARCH = 'https://lite-api.jup.ag/tokens/v2/search';
// Kursverlauf für die 7-Tage-Sparkline (die Suche liefert keinen) — OHLC-Kerzen.
// 4_HOUR × 42 = 7 Tage, gleiche Spanne wie CoinGeckos sparkline_in_7d.
const JUP_CHART = 'https://datapi.jup.ag/v2/charts';

// Session-Cache: SYM → {id, entry} | null (Fehlschlag/kein Treffer). Verhindert
// wiederholte Jupiter-Calls bei jedem loadMarket-Lauf.
const _resolved = new Map();

const up = (s) => String(s || '').trim().toUpperCase();
const cleanSym = (s) => up(s).replace(/^SPL:/, '');
const isHttps = (u) => typeof u === 'string' && /^https:\/\//i.test(u);

// Wähle aus dem Jupiter-Suchergebnis den passenden Token: erst exakte Mint,
// sonst exakte Symbol-Gleichheit mit höchster Liquidität (echtes Projekt schlägt
// Namensvetter/Scam-Klon).
function pickToken(arr, { mint, sym }) {
  if (!Array.isArray(arr) || !arr.length) return null;
  if (mint) {
    const exact = arr.find((t) => t?.id === mint);
    if (exact) return exact;
  }
  if (sym) {
    const S = up(sym);
    const matches = arr.filter((t) => up(t?.symbol) === S);
    if (matches.length) {
      return matches.sort((a, b) => (b?.liquidity || 0) - (a?.liquidity || 0))[0];
    }
  }
  return null;
}

function todayISO() {
  // Client-Kontext: new Date() ist hier erlaubt. ECB-Kurs des Handelstags.
  try { return new Date().toISOString().slice(0, 10); } catch { return null; }
}

// Baut den marketData-Eintrag aus einem Jupiter-Token + USD→EUR-Kurs.
function toEntry(t, mint, usdEurRate) {
  const rate = (typeof usdEurRate === 'number' && usdEurRate > 0) ? usdEurRate : 1;
  return {
    id: `spl:${t.id || mint}`,
    entry: {
      price: (typeof t.usdPrice === 'number' ? t.usdPrice : 0) * rate,
      chg: typeof t?.stats24h?.priceChange === 'number' ? t.stats24h.priceChange * 100 : 0,
      chg7d: null,
      mcap: typeof t.mcap === 'number' ? t.mcap : null,
      image: isHttps(t.icon) ? t.icon : null,
      spark: null,
      _spl: true,
    },
  };
}

// 7-Tage-Sparkline aus Jupiter-OHLC-Kerzen (close-Werte). Best-Effort: schlägt
// der Abruf fehl, bleibt spark null (kein Graph, Preis/Icon unberührt).
async function fetchSplSpark(mint) {
  if (!mint) return null;
  try {
    const to = Date.now(); // Client-Kontext (ms)
    const r = await fetch(`${JUP_CHART}/${mint}?interval=4_HOUR&candles=42&to=${to}`, {
      signal: typeof AbortSignal?.timeout === 'function' ? AbortSignal.timeout(8000) : undefined,
    });
    if (!r.ok) return null;
    const j = await r.json();
    const closes = (j?.candles || [])
      .map((c) => Number(c?.close))
      .filter((n) => Number.isFinite(n) && n > 0);
    return closes.length >= 2 ? closes : null;
  } catch { return null; }
}

// Mutiert COIN_MAP synthetisch, damit `COIN_MAP[sym].id → marketData[id]` greift.
// Überschreibt nur Leer-/aufgelöste/SPL-Einträge — nie einen echten COIN_MAP-Coin.
function patchCoinMap(S, id, name) {
  const cur = COIN_MAP[S];
  if (!cur?.id || cur?._resolved || cur?._spl) {
    COIN_MAP[S] = { sym: S, id, name: name || S, color: '#888', bg: '#1a1a22', _spl: true };
  }
}

/**
 * Löst EIN SPL-Token auf. @returns {Promise<{id, entry}|null>}
 * @param {{sym:string, mint?:string}} tok
 * @param {number} usdEurRate  1 USD = X EUR
 */
export async function resolveSplToken(tok, usdEurRate) {
  const S = cleanSym(tok?.sym);
  if (!S) return null;
  if (_resolved.has(S)) return _resolved.get(S);

  const mint = tok?.mint && /^[A-Za-z0-9]{32,44}$/.test(tok.mint) ? tok.mint : null;
  const query = mint || S;
  try {
    const r = await fetch(`${JUP_SEARCH}?query=${encodeURIComponent(query)}`, {
      signal: typeof AbortSignal?.timeout === 'function' ? AbortSignal.timeout(8000) : undefined,
    });
    if (!r.ok) { _resolved.set(S, null); return null; }
    const arr = await r.json();
    const t = pickToken(arr, { mint, sym: S });
    if (!t || typeof t.usdPrice !== 'number' || !(t.usdPrice > 0)) { _resolved.set(S, null); return null; }
    const { id, entry } = toEntry(t, mint, usdEurRate);
    // 7-Tage-Sparkline nachladen (eigene Chart-API; Suche liefert keine).
    const spark = await fetchSplSpark(t.id || mint);
    if (spark) entry.spark = spark;
    patchCoinMap(S, id, t.name);
    const res = { id, entry };
    _resolved.set(S, res);
    return res;
  } catch {
    _resolved.set(S, null);
    return null;
  }
}

/**
 * Löst mehrere SPL-Token auf (ein gemeinsamer USD→EUR-Kurs).
 * @param {Array<{sym:string, mint?:string}>} tokens
 * @returns {Promise<Object>} marketData-Patch { [id]: entry }
 */
export async function resolveSplMarket(tokens) {
  const list = (tokens || []).filter((t) => cleanSym(t?.sym));
  if (!list.length) return {};
  let rate = 1;
  try { rate = (await getFiatToEurRate('USD', todayISO())) || 1; } catch { rate = 1; }

  const patch = {};
  // Cap: Long-Tail bleibt überschaubar, Jupiter-freundlich.
  await Promise.all(list.slice(0, 40).map(async (tok) => {
    const res = await resolveSplToken(tok, rate);
    if (res) patch[res.id] = res.entry;
  }));
  return patch;
}

/** Nur für Tests. */
export function _clearSplCache() { _resolved.clear(); }
